import { AiError } from './types'

/**
 * Helpers de red compartidos por los adapters de IA.
 *
 * El navegador llama directamente a las APIs de OpenAI/Anthropic en el
 * modo "directo" (BYOK). Esas APIs suelen bloquear peticiones desde el
 * navegador por CORS o exigir cabeceras no permitidas; el síntoma es un
 * `TypeError: Failed to fetch` genérico que al usuario no le dice nada.
 *
 * `fetchOrThrow` envuelve el fetch y traduce ese fallo de red en un
 * `AiError` accionable que sugiere usar el modo proxy.
 */

/** ¿Parece un fallo de red/CORS (no un error HTTP con respuesta)? */
export function isNetworkError(e: unknown): boolean {
  if (!(e instanceof TypeError)) return false
  // El mensaje exacto varía, pero los navegadores usan "Failed to fetch"
  // o "NetworkError" para CORS/bloqueo/DNS/caída.
  return /failed to fetch|networkerror|load failed/i.test(e.message)
}

/**
 * Ejecuta un fetch y, si falla por red/CORS, lanza un AiError con un
 * mensaje claro que sugiere el modo proxy.
 *
 * @param fetchImpl Fetch inyectable (solo para tests); por defecto el global.
 */
export async function fetchOrThrow(
  input: string,
  init: RequestInit,
  providerLabel: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  try {
    return await fetchImpl(input, init)
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new AiError('Generación cancelada.', 499)
    }
    if (isNetworkError(e)) {
      throw new AiError(
        `${providerLabel}: no se pudo conectar desde el navegador (posible bloqueo CORS). ` +
          'Usa el modo Proxy en Ajustes → IA para llamar al proveedor desde un servidor.',
        0,
      )
    }
    throw e
  }
}
